import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DayNavigator } from '../../components/mobile/DayNavigator';
import { WorkerGroupHeader } from '../../components/mobile/WorkerGroupHeader';
import { AssignmentCard } from '../../components/mobile/AssignmentCard';
import { AssignmentSheet } from '../../components/mobile/AssignmentSheet';
import { StaleScheduleNotice } from '../../components/schedule/StaleScheduleNotice';
import {
  ScheduleFiltersSheet,
  type FilterOption,
} from '../../components/mobile/ScheduleFiltersSheet';
import {
  MOBILE_EMPTY_ACTION,
  MobileEmptyState,
  MobileErrorState,
  MobileListSkeleton,
} from '../../components/mobile/MobileListStates';
import { useSwipe } from '../../components/mobile/useSwipe';
import { scheduleQueryKey, useSchedule, fetchSchedule } from '../../hooks/useSchedule';
import { useScheduleFilterState } from '../../hooks/useScheduleFilterState';
import { useOffices } from '../../hooks/useOffices';
import { useServiceLines } from '../../hooks/useServiceLines';
import { useRegions } from '../../hooks/useRegions';
import {
  addDays,
  formatShortWeekday,
  todayIso,
} from '../../lib/date';
import { EMPTY_FILTERS, filterChips, filterCount } from '../../lib/scheduleFilters';
import type { ScheduleCellAssignment } from '../../lib/types';

/** Chips beyond this fold into a `+N more` chip that opens the sheet. */
const MAX_VISIBLE_CHIPS = 4;

const CONTROL_BUTTON =
  'min-h-[44px] px-3 rounded-lg border text-sm font-medium flex items-center gap-2';

/**
 * `/schedule` on a phone: one day at a time, grouped by worker.
 *
 * The desktop grid's whole premise — many crew down the left, many dates across the top —
 * doesn't survive 393px. So the mobile view keeps the *question* ("who's on what today?")
 * and drops the matrix: a one-day window, a list of workers, and the assignments under each.
 * Moving between days is a chevron, a swipe, or the native date picker.
 */
export function ScheduleMobilePage() {
  const [day, setDay] = useState(todayIso());
  const [sheetJob, setSheetJob] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Which failure the user has already waved away, by its timestamp — a later failure gets
  // to interrupt again, the same one doesn't.
  const [dismissedErrorAt, setDismissedErrorAt] = useState<number | null>(null);

  const { filters, changeFilters, appliedFilter, applySavedFilter, personIds, projectIds } =
    useScheduleFilterState();
  const { data: regions } = useRegions();
  const { data: offices } = useOffices();
  const { data: serviceLines } = useServiceLines();

  const commonOptions = {
    region: filters.region || undefined,
    office: filters.office || undefined,
    serviceLine: filters.serviceLine || undefined,
    personIds,
    projectIds,
  };

  const scheduleQuery = useSchedule({ from: day, to: day, ...commonOptions });

  // Warm the neighbouring days once the current one has landed, so a swipe renders from cache
  // instead of blanking to a skeleton. A one-day window is small enough that two speculative
  // fetches are cheap even on cellular, and they're the difference between "instant" and "a
  // spinner every time I move a day".
  const queryClient = useQueryClient();
  const settled = scheduleQuery.isSuccess;
  useEffect(() => {
    if (!settled) return;
    const idle = window.setTimeout(() => {
      for (const neighbour of [addDays(day, 1), addDays(day, -1)]) {
        const options = { from: neighbour, to: neighbour, ...commonOptions };
        void queryClient.prefetchQuery({
          queryKey: scheduleQueryKey(options),
          queryFn: () => fetchSchedule(options),
        });
      }
    }, 300);
    return () => window.clearTimeout(idle);
    // `commonOptions` is rebuilt every render; its *contents* are what matter, and they're all
    // primitives or the memoized id arrays from useScheduleFilterState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, day, queryClient, filters.region, filters.office, filters.serviceLine, personIds, projectIds]);

  const swipe = useSwipe({
    onSwipeLeft: () => setDay((d) => addDays(d, 1)),
    onSwipeRight: () => setDay((d) => addDays(d, -1)),
  });

  const rows = scheduleQuery.data?.rows ?? [];
  const groups = rows
    .map((row) => ({
      row,
      items: (row.cells.find((c) => c.date === day) ?? row.cells[0])?.items ?? [],
    }))
    .filter((g) => g.items.length > 0);

  const assignmentCount = groups.reduce((sum, g) => sum + g.items.length, 0);

  const regionOptions: FilterOption[] = (regions ?? []).map((r) => ({
    value: r.code,
    label: r.name || r.code,
  }));
  const regionLabel = regionOptions.find((r) => r.value === filters.region)?.label;
  const chips = filterChips(filters, regionLabel);
  const activeCount = filterCount(filters);

  function handleCardClick(a: ScheduleCellAssignment) {
    setSheetJob(a.jobNumber);
  }

  const dayLabel = formatShortWeekday(day);
  const hasCachedData = !!scheduleQuery.data;
  const showErrorBlock = !!scheduleQuery.error && !hasCachedData;
  // A day already on screen beats a blank screen in the field, so a failure with cached data
  // is a strip over the stale list rather than a replacement for it.
  const showErrorStrip =
    !!scheduleQuery.error && hasCachedData && scheduleQuery.errorUpdatedAt !== dismissedErrorAt;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Everything above the list stays put: the day never scrolls off, so a technician
          always knows which day they're reading and can always reach the filters. */}
      <div className="shrink-0 bg-white border-b border-slate-200">
        <DayNavigator
          date={day}
          onPrev={() => setDay((d) => addDays(d, -1))}
          onNext={() => setDay((d) => addDays(d, 1))}
          onToday={() => setDay(todayIso())}
          onDateChange={setDay}
        />

        <div className="px-3 pt-2 pb-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filtersOpen}
            aria-label={activeCount > 0 ? `Filters, ${activeCount} applied` : 'Filters'}
            className={`${CONTROL_BUTTON} shrink-0 ${
              activeCount > 0
                ? 'border-brand-blue text-brand-blue bg-brand-blue/5'
                : 'border-slate-300 text-brand-charcoal'
            }`}
          >
            <FilterIcon />
            <span>Filters</span>
            {activeCount > 0 && (
              <span
                aria-hidden="true"
                className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-brand-blue text-white text-xs font-semibold tabular-nums flex items-center justify-center"
              >
                {/* Capped at two digits so the button's width stays bounded — the control
                    row's fit at 375px is calculated against that ceiling. */}
                {activeCount > 99 ? '99+' : activeCount}
              </span>
            )}
          </button>
        </div>

        {chips.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar snap-x px-3 pb-2">
            {chips.slice(0, MAX_VISIBLE_CHIPS).map((chip) => (
              <span
                key={chip.key}
                className="shrink-0 inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full bg-slate-100 text-brand-charcoal text-xs font-medium"
              >
                <span className="max-w-[140px] truncate">{chip.label}</span>
                <button
                  type="button"
                  onClick={() => changeFilters(chip.without)}
                  aria-label={`Remove filter: ${chip.label}`}
                  className="w-8 h-8 -mr-1 flex items-center justify-center text-slate-500 text-base leading-none"
                >
                  ×
                </button>
              </span>
            ))}
            {/* Never leave a filter reachable only by horizontal scroll — a hidden filter is
                how a technician concludes the schedule is empty. */}
            {chips.length > MAX_VISIBLE_CHIPS && (
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="shrink-0 inline-flex items-center h-8 px-3 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-medium"
              >
                +{chips.length - MAX_VISIBLE_CHIPS} more
              </button>
            )}
          </div>
        )}
      </div>

      <StaleScheduleNotice />

      <div className="flex-1 overflow-y-auto overscroll-contain" {...swipe}>
        {showErrorStrip && (
          <MobileErrorState
            title="Couldn't load the schedule"
            onRetry={() => void scheduleQuery.refetch()}
            isRetrying={scheduleQuery.isFetching}
            compact
            onDismiss={() => setDismissedErrorAt(scheduleQuery.errorUpdatedAt)}
          />
        )}

        {showErrorBlock && (
          <MobileErrorState
            title="Couldn't load the schedule"
            onRetry={() => void scheduleQuery.refetch()}
            isRetrying={scheduleQuery.isFetching}
          />
        )}

        {!showErrorBlock && scheduleQuery.isLoading && <MobileListSkeleton />}

        {!showErrorBlock && !scheduleQuery.isLoading && groups.length === 0 && (
          activeCount > 0 ? (
            <MobileEmptyState
              title="No matches"
              body={`No assignments on ${dayLabel} match your ${activeCount} ${
                activeCount === 1 ? 'filter' : 'filters'
              }.`}
            >
              <button
                type="button"
                onClick={() => changeFilters(EMPTY_FILTERS)}
                className={MOBILE_EMPTY_ACTION}
              >
                Clear filters
              </button>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className={MOBILE_EMPTY_ACTION}
              >
                Edit filters
              </button>
            </MobileEmptyState>
          ) : (
            <MobileEmptyState
              title="Nothing scheduled"
              body={`No crew has an assignment on ${dayLabel}.`}
            >
              {day !== todayIso() && (
                <button
                  type="button"
                  onClick={() => setDay(todayIso())}
                  className={MOBILE_EMPTY_ACTION}
                >
                  Back to today
                </button>
              )}
            </MobileEmptyState>
          )
        )}

        {/* Plain rendering, no virtualizer: a one-day window caps this at the workers who
            actually have something that day. If a region ever pushes past ~80 flattened rows,
            flatten to a `{ type: 'header' | 'card' }[]` and feed the `@tanstack/react-virtual`
            dependency that's already in package.json — don't reach for it before then. */}
        {!showErrorBlock &&
          groups.map(({ row, items }) => (
            <section key={row.personId} aria-labelledby={`grp-${row.personId}`}>
              <WorkerGroupHeader
                id={`grp-${row.personId}`}
                displayName={row.displayName}
                count={items.length}
              />
              <ul>
                {items.map((a) => (
                  <li key={a.assignmentId}>
                    <AssignmentCard
                      assignment={a}
                      dateMode="day"
                      day={day}
                      onClick={() => handleCardClick(a)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>

      {/* aria-live node stays mounted even with nothing to say — an unmounted live region
          stops announcing, so the border/padding move inside the conditional instead. */}
      <div aria-live="polite" className="shrink-0 text-center text-xs text-slate-500">
        {groups.length > 0 && (
          <p className="px-3 py-3 border-t border-slate-200">
            {`Showing ${groups.length} ${groups.length === 1 ? 'worker' : 'workers'} · ${assignmentCount} ${
              assignmentCount === 1 ? 'assignment' : 'assignments'
            }`}
          </p>
        )}
      </div>

      <AssignmentSheet jobNumber={sheetJob} onClose={() => setSheetJob(null)} />

      <ScheduleFiltersSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={changeFilters}
        appliedSavedFilter={appliedFilter}
        onApplySavedFilter={applySavedFilter}
        regions={regionOptions}
        offices={(offices ?? []).map((o) => o.name)}
        serviceLines={(serviceLines ?? []).map((s) => s.name)}
      />
    </div>
  );
}

function FilterIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden="true"
    >
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </svg>
  );
}
